import {
  FormattingToolbar,
  FormattingToolbarContext,
} from "@/components/note/editor/FormattingToolbar";
import { NoteEditorMetadata } from "@/components/note/editor/NoteEditorMetadata";
import { FatalEditorErrorNotice } from "@/components/note/FatalEditorErrorNotice";
import { MentionTooltip } from "@/components/note/MentionTooltip";
import { OutdatedClientNoteNotice } from "@/components/note/OutdatedClientNoteNotice";
import { MemCommonEditorStore } from "@/components/note/store/MemCommonEditorStore";
import { NoteEditorMode, NoteType } from "@/components/note/types";
import { MdsFloatingDropdownContent } from "@/design-system/components/dropdown/MdsFloatingDropdownContent";
import { ZIndex } from "@/domains/design/constants";
import { css, cx } from "@/domains/emotion";
import { EmotionClassStyles } from "@/domains/emotion/types";
import { clientEnvModule } from "@/modules/client-env";
import { PublicAppStore, useAppStore, usePublicAppStore } from "@/store";
import { ChatHistory } from "@/store/chat/ChatHistory";
import { IContactModel } from "@/store/contacts/types";
import { INoteObservable, NoteContentVersion } from "@/store/note/types";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import styled from "@emotion/styled";
import {
  HtmlPlaceholderPreset,
  MemCommonEditor,
  MemCommonEditorAction,
  MemCommonEditorActionKind,
  MemCommonEditorInstance,
  shouldShowFormattingToolbar,
} from "@mem-labs/common-editor";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useRef,
  MutableRefObject,
  MouseEventHandler,
  useState,
} from "react";
import { useDebounceCallback } from "usehooks-ts";
import { useTheme } from "@/mds";
import { NoteEditorBottomBar } from "@/components/note/editor/source-bar/NoteEditorBottomBar";
import { NoteObservable } from "@/store/note";
import { useNoteVersionHistory } from "@/components/note/hooks/useNoteVersionHistory";
import { EditorSpinner } from "./EditorSpinner";
import { EmptyNoteActions } from "@/components/note/editor/EmptyNoteActions";
import { useMaybeTabContext } from "@/components/layout/TabContext";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";

type NoteObservableEditorProps = EmotionClassStyles & {
  editorStore?: MemCommonEditorStore;
  setEditorStore: (store: MemCommonEditorStore | undefined) => void;
  autoFocus?: boolean;
  footer?: React.ReactNode;
  chatHistory?: ChatHistory;
  goToMention?: INotesViewPageStore["goToMention"];
  onMentionMouseOver?: INotesViewPageStore["onMentionMouseOver"];
  onMentionMouseOut?: INotesViewPageStore["onMentionMouseOut"];
  openFileUploadRejectedModal?: INotesViewPageStore["openFileUploadRejectedModal"];
  openImageUploadRejectedModal?: INotesViewPageStore["openImageUploadRejectedModal"];
  noteObservable: INoteObservable | TemplateObservable;
  myAccount?: IContactModel;
  placeholder?: string;
  showMetadata?: boolean;
  showTopPadding?: boolean;
  mode: NoteEditorMode;
  highlightText?: string;
  onReadOnlyNoteClicked?: () => void;
  noteType?: NoteType;
  versions?: NoteContentVersion[];
  onNoteRendered?: (noteId: string) => void;
  htmlPlaceholderPreset?: HtmlPlaceholderPreset;
  containerWidth?: number;
  disableGlobalListeners?: boolean;
  editorModeClassName?: string;
};

export const NoteObservableEditor = observer<NoteObservableEditorProps>(
  function NoteObservableEditor({
    editorStore,
    setEditorStore,
    containerWidth,
    autoFocus = false,
    className,
    footer,
    chatHistory,
    goToMention,
    openFileUploadRejectedModal,
    openImageUploadRejectedModal,
    noteObservable,
    myAccount,
    placeholder,
    showMetadata = true,
    showTopPadding = true,
    mode,
    highlightText,
    onReadOnlyNoteClicked,
    noteType = NoteType.Note,
    onNoteRendered,
    htmlPlaceholderPreset,
    disableGlobalListeners = false,
    editorModeClassName,
  }) {
    const { theme, themeType } = useTheme();
    const { pageStore } = useAppStore();
    const maybeTab = useMaybeTabContext();
    const notesViewPage: NotesViewPageStore | undefined = maybeTab
      ? (pageStore.getPageStore(
          maybeTab.activeTab,
          maybeTab.activeTabPath,
          "notesViewPage"
        ) as NotesViewPageStore)
      : undefined;
    const { publicStore } = usePublicAppStore();

    const [editorContainer, setEditorContainer] = useState<HTMLDivElement | null>(null);
    const appendTo = useCallback(() => {
      if (!editorContainer) return document.body;

      let el: HTMLElement = editorContainer;
      while (el.parentElement && el.clientHeight > 0) {
        el = el.parentElement;
      }
      return el;
    }, [editorContainer]);

    const editorInstanceRef = useRef<MemCommonEditorInstance | null>(null);
    const dispatchAction = useCallback(
      (event: MemCommonEditorAction) => {
        if (
          mode !== NoteEditorMode.Editable &&
          event.kind !== MemCommonEditorActionKind.ApplyRemoteUpdate
        )
          return;

        return editorInstanceRef.current?.dispatchAction?.(event);
      },
      [mode]
    );

    // Sync note version history state with editor diff display
    useNoteVersionHistory(editorStore);

    useHighlighting({
      highlightText,
      editorInstanceRef,
      mode,
    });

    const onEditorRendered = useCallback(
      (el: HTMLSpanElement | null) => {
        if (!el || !editorStore?.id) return;
        onNoteRendered?.(editorStore?.id);
      },
      [editorStore?.id, onNoteRendered]
    );

    // Preserve a ref in case state is reset before the component unmounts
    const editorRef = useRef<MemCommonEditorStore>();

    useUnmountEditorStore(editorRef, noteObservable);
    useNote(editorStore, noteObservable, setEditorStore, publicStore);

    // After editorStore is nullified we can reset it
    useEffect(() => {
      if (editorStore) return;

      const newStore = new MemCommonEditorStore({
        autoFocus,
        disableGlobalListeners,
        dispatchAction,
        goToMention,
        openFileUploadRejectedModal,
        openImageUploadRejectedModal,
        noteObservable,
        myAccount,
        placeholder,
        publicStore,
        chatHistory,
        onReadOnlyNoteClicked,
        mode,
        theme,
        themeType,
        noteType,
        htmlPlaceholderPreset,
      });

      editorRef.current = newStore;
      setEditorStore(newStore);

      /**
       * The `MemCommonEditorStore` is tightly coupled to our ui - we need to tie
       * some of its lifecycle methods to our component lifecycle.
       *
       * This will ensure the contents of the editor remain up-to-date (E.g. when
       * receiving remote updates).
       *
       * If `mode` is set to "static", the editor will not have any lifecycle
       * methods registered.
       */

      if (mode === NoteEditorMode.Static) {
        return;
      }

      newStore.mount();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorStore]);

    useEffect(() => {
      editorStore?.setTheme({ theme, themeType });
    }, [editorStore, theme, themeType]);

    const focusEditor: React.MouseEventHandler<HTMLDivElement> = useCallback(
      e => {
        if (mode !== NoteEditorMode.Editable) return;
        e.stopPropagation();

        const editor = editorInstanceRef.current?.editor;
        if (!editor?.isEditable || editor?.isFocused) return;

        // This click can only happen above the editor (otherwise the editor would handle it instead)
        // so we focus the editor and put the cursor at the beginning of the document.
        editor.chain().focus().setTextSelection(0).run();
      },
      [mode]
    );

    const handleScroll = useDebounceCallback(
      () => {
        dispatchAction({
          kind: MemCommonEditorActionKind.ResendMentionUpdate,
          payload: null,
        });
      },
      20,
      { maxWait: 40 }
    );

    const handleMouseMove: MouseEventHandler<HTMLDivElement> = useDebounceCallback(
      event => {
        editorStore?.setMouseY(event.clientY);
      },
      20,
      { maxWait: 40 }
    );

    const showFormattingToolbar = useCallback(
      () => shouldShowFormattingToolbar(editorInstanceRef.current?.editor),
      []
    );

    if (!editorStore) {
      return <div className={className} translate="no"></div>;
    }

    const insertMenuContentClassName = css`
      max-width: calc(100vw - 20px);
      display: inline-flex;
      min-width: 176px;
      padding: ${theme.spacing[3]} 0;
      justify-content: center;
      gap: ${theme.spacing.smPlus};
      width: ${editorStore?.isInsertMenuActive ? "288px" : "340px"};
      flex-direction: column;
      align-items: flex-start;
      background: ${theme.colors.surface.menu};
      border-radius: ${theme.borderRadius.large};
      border: 1px solid ${theme.colors.border.surface};
      background: ${theme.colors.surface.menu};
      box-shadow: ${theme.shadows.surface};

      & > div {
        width: 100%;
      }
    `;

    const hasSupportedSources =
      noteObservable instanceof NoteObservable
        ? (noteObservable.sourceList?.allSupportedSources?.length ?? 0) > 0
        : false;

    return (
      <FormattingToolbarContext.Provider value={editorStore}>
        {editorStore.editor && !!editorContainer && (
          <FormattingToolbar
            appendTo={appendTo}
            editor={editorStore.editor}
            shouldShow={showFormattingToolbar}
            editorRef={editorInstanceRef}
          />
        )}
        <div
          className={cx(className, editorModeClassName, editorContainerStyles)}
          translate="no"
          onScroll={handleScroll}
          onMouseMoveCapture={handleMouseMove}
          ref={setEditorContainer}
        >
          {showTopPadding && <TopPadding onClick={focusEditor} className={editorModeClassName} />}
          {showMetadata && (
            <NoteEditorMetadata
              className={editorModeClassName}
              store={editorStore}
              onClick={focusEditor}
            />
          )}
          {!editorStore.reloadRequired && (
            <>
              {!editorStore.hasContentError && !editorStore.hasFatalError && (
                <>
                  <MemCommonEditor
                    editorInstanceRef={editorInstanceRef}
                    editorInitializer={editorStore.editorInitializer}
                    editorEventHandler={editorStore.editorEventHandler}
                    uiTheme={editorStore.uiTheme}
                    footer={footer}
                  />
                  <Invisible ref={onEditorRendered} />
                </>
              )}
              {editorStore.hasContentError && (
                <OutdatedClientNoteNotice
                  noteId={noteObservable.id}
                  isDesktop={clientEnvModule.isDesktop()}
                />
              )}
              {editorStore.hasFatalError && (
                <FatalEditorErrorNotice
                  noteId={noteObservable.id}
                  isDesktop={clientEnvModule.isDesktop()}
                />
              )}
            </>
          )}
          <MdsFloatingDropdownContent
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.mentionClientRect}
            placement="bottom-start"
            onHover={editorStore.handleDropdownHover}
            contentListClassName={insertMenuContentClassName}
            contentList={editorStore.mentionContentList}
          />
          <MdsFloatingDropdownContent
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.tableMenuClientRect}
            placement={editorStore.tableMenuPlacement}
            offsetOptions={editorStore.tableMenuOffsetOptions}
            contentList={editorStore.tableMenuContentList}
          />
          <MentionTooltip
            mentionTooltipParams={editorStore.mentionTooltipParams}
            resetMentionTooltipParams={editorStore.resetMentionTooltipParams}
          />
          {/* Overlay spinner for version transitions */}
          <EditorSpinner
            isOverlay
            hideDelay={300}
            show={
              !!notesViewPage?.noteVersionHistory?.isLoading &&
              notesViewPage?.noteVersionHistory?.currentNoteId === editorStore.noteObservable?.id
            }
          />
        </div>
        {mode === NoteEditorMode.Editable && (
          <NoteActions
            editorStore={editorStore}
            versionHistoryActive={
              notesViewPage?.noteVersionHistory?.currentNoteId == noteObservable.id
            }
          />
        )}
        {noteObservable instanceof NoteObservable &&
          (hasSupportedSources || !editorStore.isNoteEmpty) &&
          mode === NoteEditorMode.Editable &&
          notesViewPage?.noteVersionHistory?.currentNoteId !== noteObservable.id && (
            <NoteEditorBottomBar
              note={noteObservable}
              tooltipLabel={editorStore.metadataTooltip}
              containerWidth={containerWidth}
            />
          )}
      </FormattingToolbarContext.Provider>
    );
  }
);

const NoteActions = observer(
  ({ editorStore }: { editorStore: MemCommonEditorStore; versionHistoryActive: boolean }) => {
    if (editorStore.isNoteEmpty) {
      return <EmptyNoteActions editorStore={editorStore} />;
    }
    return null;
  }
);

const TopPadding = styled.div`
  padding-top: ${({ theme }) => theme.spacing["5"]};
`;

// Free up store when component unmounts
const useUnmountEditorStore = (
  editorRef: MutableRefObject<MemCommonEditorStore | undefined>,
  noteObservable: INoteObservable | TemplateObservable
) => {
  useEffect(() => {
    return () => {
      if (editorRef?.current && noteObservable) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        editorRef.current.unmount();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// Reset editor store to allow TitTap to re-initialize
const useNote = (
  editorStore: MemCommonEditorStore | undefined,
  noteObservable: INoteObservable | TemplateObservable,
  setEditorStore: (store: MemCommonEditorStore | undefined) => void,
  publicStore: PublicAppStore
) => {
  useEffect(() => {
    if (!editorStore) return;
    if (editorStore.id === noteObservable.id) return;

    if (noteObservable) {
      editorStore.unmount();
    }

    setEditorStore(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteObservable.id, publicStore]);
};

// Send highlight actions to the editor
interface UseHighlightingArg {
  highlightText: string | undefined;
  editorInstanceRef: MutableRefObject<MemCommonEditorInstance | null>;
  mode: NoteEditorMode;
}
const useHighlighting = ({ highlightText, editorInstanceRef, mode }: UseHighlightingArg) => {
  // Send a highlight command to the editor when its available, based on the highlight prop
  useEffect(() => {
    const animate = mode === NoteEditorMode.Editable;

    // when the user moves from one hover to another without the floating note preview being closed,
    // we need to clear out the previous highlight to avoid some jank
    if (editorInstanceRef.current) {
      editorInstanceRef.current?.dispatchAction?.({
        kind: MemCommonEditorActionKind.HighlightText,
        payload: {
          text: "",
          animate: false,
        },
      });
    }

    // Wait for the editor to be initialized and then highlight the text.
    // NOTE: Even if the instance was available in the above block, we should still use
    // this interval in case the editor is being re-initialized.
    let editorWatcherInterval: ReturnType<typeof setInterval> | null = setInterval(() => {
      if (editorInstanceRef.current?.dispatchAction) {
        if (highlightText) {
          editorInstanceRef.current.dispatchAction({
            kind: MemCommonEditorActionKind.HighlightText,
            payload: { text: highlightText, animate },
          });
        }

        // clear the interval
        if (editorWatcherInterval) {
          clearInterval(editorWatcherInterval);
          editorWatcherInterval = null;
        }
      }
    }, 50);

    return () => {
      // clear the interval
      if (editorWatcherInterval) {
        clearInterval(editorWatcherInterval);
        editorWatcherInterval = null;
      }
    };
  }, [editorInstanceRef, highlightText, mode]);
};

const Invisible = styled.span({ display: "none" });

const editorContainerStyles = css`
  position: relative;
  max-width: calc(700px + var(--extra-padding-left) + var(--extra-padding-right));
  scrollbar-width: none;
`;
